// Implements: F-IDN-026, F-UIX-018, F-UIX-010
/**
 * Invitaciones (mockup v3 P3, `vInvites`) of one organization. Mount it keyed by the organization's
 * id (checklist 14).
 *
 * Owner P2: when the mail channel is configured the server also e-mails each issued or rotated link
 * (`email_sent`) and the screen says so; the link is still shown, because it is the fallback.
 * Otherwise an invitation travels as its link. "Copiar" ROTATES the
 * invitation (`POST .../link`, Default P3-7: the link is renewed for 7 days) and copies the NEW
 * link; the previous one stops working. When the clipboard refuses (no permission, no secure
 * origin) the new link is shown in a read-only field to copy by hand (checklist 15). Issuing N
 * addresses sends N requests one after the other and shows each new link once.
 *
 * `?invite=1` (the command palette's "Invitar personas") opens the invite dialog once and is
 * dropped from the address, so going back or reloading does not open it again.
 *
 * Owner 2026-10-19: inviting a Líder to a team that already has one asks first, as adding or
 * promoting one does: "Este equipo ya tiene líder (<name>)". Cancelar (the initial focus) returns to
 * the form with what was typed; "Convertir a <name> en miembro" issues the invitation naming that
 * lead, and the hand-over happens when the invitee accepts. The list then says "Líder (reemplaza a
 * <name>)". A lead who changed meanwhile is the server's `409 lead_taken`, said per address.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useSearchParams } from "react-router";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { invitationLink } from "../../../shared/api/invitationLink";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { Tabs, tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import type { InvitationView } from "../../../types/organization";
import { InviteDialog } from "../components/InviteDialog";
import type { InviteRequest } from "../components/InviteDialog";
import { InvitationsTable } from "../components/InvitationsTable";
import { IssuedLinks } from "../components/IssuedLinks";
import type { IssuedEntry } from "../components/IssuedLinks";
import { LinkField } from "../components/LinkField";
import styles from "../components/Organization.module.css";
import {
  useInvitationList,
  useIssueInvitations,
  useRevokeInvitation,
  useRotateInvitationLink,
} from "../hooks/useInvitations";
import { useTeamList } from "../hooks/usePeople";
import { leadRefusalKey } from "../model/invitationRefusal";
import { writeProblem } from "./PeopleContainer";

type Filter = "pending" | "all";

/** The question on screen: what was asked to be sent, and the lead it would replace. */
interface LeadChoice {
  readonly request: InviteRequest;
  readonly lead: { readonly id: string; readonly name: string };
}

interface ManualCopy {
  readonly email: string;
  readonly link: string;
}

/** Write a link to the clipboard; `false` when the browser refuses (no API, no permission). */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export interface InvitationsContainerProps {
  readonly organizationId: string;
}

export function InvitationsContainer({ organizationId }: InvitationsContainerProps): ReactElement {
  const { t } = useTranslation();
  const invitations = useInvitationList(organizationId);
  const teams = useTeamList(organizationId);
  const issue = useIssueInvitations(organizationId);
  const rotate = useRotateInvitationLink(organizationId);
  const revoke = useRevokeInvitation(organizationId);
  const [filter, setFilter] = useState<Filter>("pending");
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<readonly IssuedEntry[] | null>(null);
  const [issuedCopied, setIssuedCopied] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailed, setEmailed] = useState<string | null>(null);
  const [manual, setManual] = useState<ManualCopy | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<InvitationView | null>(null);
  const [leadChoice, setLeadChoice] = useState<LeadChoice | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromAddress = useRef(false);

  useEffect(() => {
    if (openedFromAddress.current || searchParams.get("invite") !== "1") return;
    openedFromAddress.current = true;
    setInviting(true);
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const all = useMemo(() => invitations.data?.invitations ?? [], [invitations.data]);
  const pending = all.filter((invitation) => invitation.status === "pending");
  const shown = filter === "pending" ? pending : all;
  const teamOptions = useMemo(
    () => (teams.data?.teams ?? []).map((team) => ({ id: team.id, name: team.name })),
    [teams.data],
  );
  const teamNames = useMemo(() => new Map(teamOptions.map((team) => [team.id, team.name] as const)), [teamOptions]);
  const personNames = useMemo(
    () =>
      new Map(
        (teams.data?.teams ?? []).flatMap((team) => team.members.map((member) => [member.user.id, member.user.full_name] as const)),
      ),
    [teams.data],
  );

  /** The current lead of `teamId` as this screen knows it, or `null`. */
  function leadOf(teamId: string | null): LeadChoice["lead"] | null {
    const team = (teams.data?.teams ?? []).find((entry) => entry.id === teamId);
    const lead = team?.members.find((member) => member.role === "team_lead");
    return lead === undefined ? null : { id: lead.user.id, name: lead.user.full_name };
  }

  function copy(invitation: InvitationView): void {
    if (copying !== null) return;
    setCopying(invitation.id);
    setCopied(false);
    setEmailed(null);
    setManual(null);
    setProblem(null);
    rotate.mutate(invitation.id, {
      onSuccess: (rotated) => {
        const link = invitationLink(rotated.token);
        setEmailed(rotated.email_sent ? invitation.email : null);
        void copyToClipboard(link).then((ok) => {
          if (ok) setCopied(true);
          else setManual({ email: invitation.email, link });
        });
      },
      onError: (error) => {
        setProblem(writeProblem(asApiError(error), t("common.state.error")));
      },
      onSettled: () => {
        setCopying(null);
      },
    });
  }

  function invite(request: InviteRequest): void {
    const lead = request.teamRole === "team_lead" ? leadOf(request.teamId) : null;
    if (lead !== null) {
      setLeadChoice({ request, lead });
      return;
    }
    send(request);
  }

  function send(request: InviteRequest, replaceLeadUserId?: string): void {
    if (issue.isPending) return;
    issue.mutate(
      {
        emails: request.emails,
        role: request.role,
        teamId: request.teamId,
        teamRole: request.teamRole,
        ...(replaceLeadUserId === undefined ? {} : { replaceLeadUserId }),
      },
      {
        onSuccess: (outcomes) => {
          setLeadChoice(null);
          setInviting(false);
          setFilter("pending");
          setIssuedCopied(null);
          setIssued(
            outcomes.map((outcome) =>
              outcome.issued === undefined
                ? { email: outcome.email, problem: issueProblem(outcome.error) }
                : {
                    email: outcome.email,
                    link: invitationLink(outcome.issued.token),
                    emailed: outcome.issued.email_sent,
                  },
            ),
          );
        },
      },
    );
  }

  function issueProblem(error: ApiError): string {
    const lead = leadRefusalKey(error.details);
    return lead === null ? writeProblem(error, t("common.state.error")) : t(lead);
  }

  function confirmRevoke(): void {
    if (revoking === null) return;
    const target = revoking;
    setRevoking(null);
    setProblem(null);
    revoke.mutate(target.id, {
      onError: (error) => {
        setProblem(writeProblem(asApiError(error), t("common.state.error")));
      },
    });
  }

  function body(): ReactElement {
    if (invitations.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(invitations.error)}
          onRetry={() => {
            void invitations.refetch();
          }}
        />
      );
    }
    if (invitations.data === undefined) return <LoadingState label={t("organization.invitations.loading")} />;
    if (shown.length === 0) {
      return (
        <EmptyState
          headingLevel={3}
          icon="mail"
          title={t("organization.invitations.empty.title")}
          body={t("organization.invitations.empty.body")}
        />
      );
    }
    return (
      <InvitationsTable
        invitations={shown}
        teamNames={teamNames}
        personNames={personNames}
        copying={copying}
        onCopy={copy}
        onRevoke={setRevoking}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Tabs
          label={t("organization.invitations.tabs")}
          idBase="invitations"
          items={[
            { id: "pending", label: t("organization.invitations.tab.pending"), count: pending.length },
            { id: "all", label: t("organization.invitations.tab.all"), count: all.length },
          ]}
          selected={filter}
          onSelect={(id) => {
            setFilter(id === "all" ? "all" : "pending");
          }}
        />
        <span className={styles.spacer} />
        <Button
          variant="primary"
          hasPopup="dialog"
          onClick={() => {
            setInviting(true);
          }}
        >
          <Icon name="user-plus" size="sm" />
          {t("organization.invitations.invite")}
        </Button>
      </div>
      <p className={styles.notice}>{t("organization.invitations.notice")}</p>
      {copied ? (
        <p className={styles.status} role="status">
          {t("organization.invitations.copied")}
        </p>
      ) : null}
      {emailed === null ? null : (
        <p className={styles.status} role="status">
          {t("organization.issued.emailed", { email: emailed })}
        </p>
      )}
      {manual === null ? null : (
        <div className={styles.form} role="status">
          <p className={styles.status}>{t("organization.invitations.copyFailed")}</p>
          <LinkField
            id="invitation-manual-link"
            label={t("organization.invitations.linkFor", { email: manual.email })}
            value={manual.link}
          />
        </div>
      )}
      {problem === null ? null : (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
      <div {...tabPanelProps("invitations", filter)}>{body()}</div>
      <ConfirmDialog
        open={revoking !== null}
        title={t("organization.invitations.revoke.title")}
        body={revoking === null ? "" : t("organization.invitations.revoke.body", { email: revoking.email })}
        confirmLabel={t("organization.invitations.revoke")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={confirmRevoke}
        onCancel={() => {
          setRevoking(null);
        }}
      />
      {inviting ? (
        <InviteDialog
          teams={teamOptions}
          pending={issue.isPending}
          hidden={leadChoice !== null}
          onSubmit={invite}
          onCancel={() => {
            setInviting(false);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={leadChoice !== null}
        title={t("organization.team.leadTaken.title", { name: leadChoice?.lead.name ?? "" })}
        body={t("organization.invite.leadTaken.body", { name: leadChoice?.lead.name ?? "" })}
        confirmLabel={t("organization.team.leadTaken.convert", { name: leadChoice?.lead.name ?? "" })}
        cancelLabel={t("common.action.cancel")}
        confirmLoading={issue.isPending}
        onConfirm={() => {
          if (leadChoice !== null) send(leadChoice.request, leadChoice.lead.id);
        }}
        onCancel={() => {
          // While the invitation is on its way the question stays until the answer.
          if (!issue.isPending) setLeadChoice(null);
        }}
      />
      {issued === null ? null : (
        <IssuedLinks
          entries={issued}
          copied={issuedCopied}
          onCopy={(entry) => {
            void copyToClipboard(entry.link).then((ok) => {
              setIssuedCopied(ok ? entry.email : null);
            });
          }}
          onClose={() => {
            setIssued(null);
          }}
        />
      )}
    </div>
  );
}
